import React, {createElement} from 'react';
import moment from 'moment';
import {idealRanges, idealRangesFormatted, fillStars, getIdealRange, getMinMax} from 'helpers/dataHelpers';
import ConstraintAlertField from './../../components/constraintAlertField.jsx';
import {countries} from 'helpers/constants';


export const IDEAL_TESTS = 4;
const LEAVE_BLANK = 'LEAVE_BLANK';


export default function generateDetailedView(intl, dealer, branch, pool, products, columns = [],
                                             formatMessage, separateIdealsColumn = true, fillBlank = false) {
  let tableRows = [];
  let numAnalyses = columns.length;
  let idealRanges = idealRangesFormatted(formatMessage);

  if (dealer.poolScores) {
    insertPoolScores(separateIdealsColumn, columns, tableRows, fillBlank, formatMessage);
  }

  for (let key in idealRanges) {
    if(key) {
      let [analyses, numNotTested, numNos] = calculateRow(intl, dealer, pool, products, columns, key, dealer);

      let ideal = getIdealRange(key, columns[0], pool, products, dealer.country, dealer.country === countries.CA);

      if ((numNotTested < numAnalyses && numNos < numAnalyses || idealRanges[key].required) &&
      (!idealRanges[key].condition || idealRanges[key].condition(columns[0], pool, products))) {
        let title = idealRanges[key].title;

        if (typeof title === 'function') {
          title = title(branch, pool);
        }

        if (!separateIdealsColumn && ideal) {
          title = <div>{title}<span className='pull-right'>({ideal})</span></div>;
        }

        let separator = idealRanges[key].separator ? 'tablerow-separator' : null;

        if (idealRanges[key].separator && typeof idealRanges[key].separator === 'function') {
          separator = idealRanges[key].separator(null, pool) ? 'tablerow-separator' : null;
        }


        tableRows.push(<tr key={'row' + key}
                         className={separator}>
          <td>{title}</td>
          {separateIdealsColumn ? <td>{ideal}</td> : null}
          {analyses}
          {fillBlank ? generateBlankCells(numAnalyses) : null}
        </tr>);
      }
    }
  }

  return tableRows;
}

export function generateBlankCells(numAnalyses, ideal = IDEAL_TESTS, type = 'td') {
  return new Array(ideal - numAnalyses).fill(undefined).map((x, i) => createElement(type, {key: i}));
}

function insertPoolScores(separateIdealsColumn = true, columns = [], tableRows = [], fillBlank = false, formatMessage) {
  tableRows.push(<tr key={'row' + 'swimmer-comfort'}>
    <td>{formatMessage({id: 'general.comfort'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'swimmerComfort' + i}>{fillStars(x.comfortScore)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);

  tableRows.push(<tr key={'row' + 'water-clarity'}>
    <td>{formatMessage({id: 'general.clarity'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'waterClarity' + i}>{fillStars(x.clarityScore)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);

  tableRows.push(<tr key={'row' + 'surface-protection'}>
    <td>{formatMessage({id: 'general.protection'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'surface-protection' + i}>{fillStars(x.protectionScore)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);
}

function calculateRow(intl, dealer, pool, products, columns = [], key = '') {
  let numNotTested = 0;
  let numNos = 0;

  let analyses = columns.map((analysis, index) => {
    let val;

    [val, numNotTested, numNos] = formatCell(intl, key, analysis, numNotTested, numNos);

    let [min, max] = getMinMax(key, analysis, pool, products, dealer.country, dealer.country === countries.CA);

    return (
      <td key={index + '.' + key}>
        <ConstraintAlertField
          value={val}
          displayOptions={idealRanges[key].displayOptions}
          min={min}
          max={max}
          exact={idealRanges[key].exact}/>
      </td>
    );
  });

  return [analyses, numNotTested, numNos];
}

function algaeProblems(formatMessage, analysis, blank = false) {
  let output = [];

  if (analysis.swampy) {
    output.push(formatMessage({id: 'general.swampy'}));
  }
  if (analysis.greenAlgae) {
    output.push(formatMessage({id: 'detailed-report.problems.algae.green'}));
  }
  if (analysis.blackAlgae === '1' || analysis.blackAlgae === '2' || analysis.blackAlgae === 'L' || analysis.blackAlgae === 'E') {
    output.push(formatMessage({id: 'detailed-report.problems.algae.black'}));
  }
  if (analysis.mustardAlgae) {
    output.push(formatMessage({id: 'detailed-report.problems.algae.mustard'}));
  }
  if (analysis.spaAlgae) {
    output.push(formatMessage({id: 'detailed-report.problems.algae.spa'}));
  }

  let algae = output.join(', ');

  if (!algae && analysis.id) {
    algae = LEAVE_BLANK;
  }

  return blank ? trueBlank(algae) : algae;
}

function waterClarityProblems(formatMessage, analysis, blank = false) {
  let output = [];

  if (analysis.hazy) {
    output.push(formatMessage({id: 'detailed-report.problems.hazy'}));
  }
  if (analysis.cloudy) {
    output.push(formatMessage({id: 'detailed-report.problems.cloudy'}));
  }
  if (analysis.foaming) {
    output.push(formatMessage({id: 'detailed-report.problems.foaming'}));
  }

  let val = output.join(', ');

  if (!val && analysis.id) {
    val = LEAVE_BLANK;
  }

  if (blank) {
    return trueBlank(val);
  }

  return val;
}

function surfaceProblems(formatMessage, analysis, blank = false) {
  let output = [];

  if (analysis.waterBuildup) {
    output.push(formatMessage({id: 'detailed-report.problems.buildup.waterline'}));
  }
  if (analysis.scaleBuildup) {
    output.push(formatMessage({id: 'detailed-report.problems.buildup.scale'}));
  }
  if (analysis.ironStaining) {
    output.push(formatMessage({id: 'detailed-report.problems.staining.iron'}));
  }
  if (analysis.copperStaining === '1' || analysis.copperStaining === '2' || analysis.copperStaining === 1 || analysis.copperStaining === 2) {
    output.push(formatMessage({id: 'detailed-report.problems.staining.copper'}));
  }
  if (analysis.organicStaining) {
    output.push(formatMessage({id: 'detailed-report.problems.staining.organic'}));
  }
  if (analysis.unknownStaining) {
    output.push(formatMessage({id: 'detailed-report.problems.staining.unknown'}));
  }

  let val = output.join(', ');

  return !val && analysis.id && !blank ? LEAVE_BLANK : trueBlank(val);
}

function chlorineDemandProblems(analysis) {
  if ((analysis.cDSample || analysis.cDSample === 0) && (analysis.cDDistStandard || analysis.cDDistStandard === 0)) {
    return `${analysis.cDSample} (${analysis.cDDistStandard})`;
  }
  return null;
}

function branchName(analysis) {
  if (!analysis || !analysis.branchID || analysis.branchID < 1 || !analysis.branchName) {
    return LEAVE_BLANK;
  }
  return analysis.branchName;
}

function formatValue({formatMessage, formatNumber}, key, analysis, val, numNotTested, numNos, overwriteDisplayValueLanguageKey = null) {
  // Format null values
  if (analysis.id && !val && val !== LEAVE_BLANK && val !== 0 && val !== false && key !== 'waterTester') {
    val = formatMessage({id: 'general.not.tested'});
    numNotTested += 1;
  }

  if (val === LEAVE_BLANK) {
    val = '';
  }

  // Format booleans
  if (val === false && analysis.id) {
    val = formatMessage({id: 'general.no'});
    numNos += 1;
  } else if (val === true) {
    val = formatMessage({id: 'general.yes'});
  }

  // Format floats
  if (val && val.toString() === parseFloat(val).toString() && val.toString() !== parseInt(val).toString()) {
    val = formatNumber(val, {minimumFractionDigits: 1, maximumFractionDigits: 1});
  }

  // Format dates
  if (val && (val.toString().match(/Date\([0-9]+\)/) || moment.isMoment(val) && val.isValid())) {
    val = moment(val).format('LL');
  }

  if (typeof val === 'string' && overwriteDisplayValueLanguageKey) {
    val = formatMessage({id: overwriteDisplayValueLanguageKey});
  }

  return [val, numNotTested, numNos];
}

export function formatCell({formatMessage, formatNumber}, key, analysis, numNotTested = 0, numNos = 0) {
  let val;
  let overwriteDisplayValueLanguageKey = null;

  if (key === 'algae') {
    val = algaeProblems(formatMessage, analysis);
  } else if (key === 'waterClarity') {
    val = waterClarityProblems(formatMessage, analysis);
  } else if (key === 'surfaceProblems') {
    val = surfaceProblems(formatMessage, analysis);
  } else if (key === 'cDSample') {
    val = chlorineDemandProblems(formatMessage, analysis);
  } else if (key === 'branchName') {
    val = branchName(analysis);
  } else {
    val = analysis[key];
  }

  // Format keys in dictionaries
  if (idealRanges[key].displayOptions && idealRanges[key].displayOptions[val]) {
    //Key to lookup  in language dictionary under general section
    overwriteDisplayValueLanguageKey = `general.${key}.${val}`;
    val = idealRanges[key].displayOptions[val];
  }

  return formatValue({formatMessage, formatNumber}, key, analysis, val,
    numNotTested, numNos, overwriteDisplayValueLanguageKey);
}

function trueBlank(value) {
  if (value === null || value === undefined || value === LEAVE_BLANK) {
    return '';
  }
  return value;
}

export function emailSummary({formatMessage, formatNumber}, analysis, pool, products, dealer, branch) {
  let data = [];

  for (let key of Object.keys(idealRanges)) {
    let title = idealRanges[key].title;

    if (typeof title === 'function') {
      title = title(branch, pool);
    }

    let ideal = getIdealRange(key, analysis, pool, products, dealer.country, dealer.country === countries.CA);
    let [value, notTested, no, displayValueLanguageKey] = formatCell({formatMessage, formatNumber},
      key, analysis, 0, 0);

    if ((notTested !== 1 && no !== 1 || idealRanges[key].required) &&
      (!idealRanges[key].condition || idealRanges[key].condition(analysis, pool, products))) {
      data.push([key, value + '', ideal, title, displayValueLanguageKey]);
    }
  }

  return {
    data,
    algaeProblems: algaeProblems(formatMessage, analysis, true),
    surfaceProblems: surfaceProblems(formatMessage, analysis, true),
    waterClarityProblems: waterClarityProblems(formatMessage, analysis, true),
    comfortScore: analysis.comfortScore,
    clarityScore: analysis.clarityScore,
    protectionScore: analysis.protectionScore,
  };
}
